import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Send, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState, Badge } from "./_ui";

const EMPTY = { subject: "", body: "", audience: "all" };

export default function CommunicationPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/communications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.subject || !form.body) return toast.error("Sujet et message requis");
    setBusy(true);
    try {
      await api.post("/admin/communications", form);
      toast.success("Communication enregistrée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/admin/communications/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Communication" /><Spinner /></>);

  return (
    <div data-testid="admin-communication">
      <PageHeader
        title="Communication"
        subtitle="Newsletters, annonces officielles et messages diffusés (mode démo — envoi simulé)."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="com-create-btn">
              <Plus size={16} /> Nouvelle communication
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Sujet" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="md:col-span-2" data-testid="com-subject" />
            <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              <option value="all">Tous (public)</option>
              <option value="members">Membres</option>
              <option value="admins">Administrateurs</option>
            </Select>
          </div>
          <Textarea placeholder="Message…" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-3" data-testid="com-body" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="com-save"><Send size={14} /> Envoyer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Megaphone} title="Aucune communication" />
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`com-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Megaphone size={15} className="text-[#D4AF37]" />
                    <h3 className="text-sm font-medium text-white">{c.subject}</h3>
                    <Badge color="blue">{c.audience}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Par {c.author} · {new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
                  <p className="text-sm text-slate-300 mt-3 whitespace-pre-line">{c.body}</p>
                </div>
                <button onClick={() => remove(c.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
